import React, { useEffect, useMemo, useRef, useState } from "react";
import { CaretRight, Close } from "@carbon/icons-react";
import { Typography } from "../Shadcn/Typography";
import { Button as ShadcnButton } from "../Shadcn/Button";

// Components
import { Heading, TypographyColor, TypographyType, Button, ButtonTheme, ButtonVariant, CustomDropdown, Chip, Body, BodyColor, BodyType, } from "../../ui-elements";

// CSS
import "../../styles/main.css";

// Utilities
import { useRecoilState, useRecoilValue, useSetRecoilState } from "recoil";
import { expandedPresenceConfigState, geoState, recommendedRegionsState, selectedTabState, spinnerState, dynamicTogglesState } from "../../states";
import { PresenceParameter } from "./PresenceParameter";
import { checkIsChecked, deepEqual, useHandleError } from "../../helpers";
import { useCIFService } from "../../services";
import equal from "fast-deep-equal";
import { useSearchParams } from "react-router-dom";

interface PresenceConfigProps {
    isChecked: { MBP: boolean; spotlight: boolean, adjacency: boolean };
    toggleSwitch: (e: any, name?: string, value?: boolean) => void;
    expandedTopDistricts: boolean;
    setExpandedTopDistricts: (expandedTopDistricts: boolean) => void;
    fetchMyPresence: (payload: any) => void;
    setMbpFilter: React.Dispatch<React.SetStateAction<any>>;
    presenceTypeCount: any[];
    mapSpinner: number;
    myPresence: { info: any[], markers: any[] };
    myBusinessPresenceConfig: any;
    setMyBusinessPresenceConfig: React.Dispatch<any>
}

interface ParamSelection {
    selectedOptions: string[];
    value?: {
        label: string;
        year: string[];
        key: string;
    };
    year?: string;
}

interface PresenceStateItem {
    parent: boolean;
    status: number[];
    params: number[];
    paramSelections?: Record<number, ParamSelection>;
    startYear: number | string;
    endYear: number | string;
}


export const PresenceConfig = ({ isChecked, toggleSwitch, expandedTopDistricts, setExpandedTopDistricts, fetchMyPresence, setMbpFilter, presenceTypeCount, mapSpinner, myPresence, myBusinessPresenceConfig, setMyBusinessPresenceConfig }: PresenceConfigProps) => {
    const [searchParams] = useSearchParams();
    const catchment = searchParams.get("catchment");
    const cifService = useCIFService();
    const selectedGeo = useRecoilValue(geoState);
    const selectedTab = useRecoilValue(selectedTabState);
    const [expandedPresenceConfig, setExpandedPresenceConfig] = useRecoilState(expandedPresenceConfigState);
    const setSpinner = useSetRecoilState(spinnerState);
    const dynamicToggles = useRecoilValue(dynamicTogglesState);
    const [presenceState, setPresenceState] = useState<Record<number, PresenceStateItem>>({});
    const [initialPresenceState, setInitialPresenceState] = useState<Record<number, PresenceStateItem>>({});
    const [buttonVisible, setButtonVisible] = useState(false);
    const { getErrorMsg } = useHandleError();
    const [openYears, setOpenYears] = useState<any>({
        startOptions: [],
        endOptions: [],
    });
    const panelRef = useRef<HTMLDivElement | null>(null);
    const geoCode = useMemo(() => {
        const { country, state, district, sub_district, village } = selectedGeo;
        const geo_id = village || sub_district || district || state || country;
        return (geo_id);
    }, [selectedGeo]);
    const mbpPayloadRef = useRef<any>(null);

    const toggleExpand = (index: number) => {
        if (expandedTopDistricts) setExpandedTopDistricts(false);
        setExpandedPresenceConfig((prev: any) => {
            if (prev === index) {
                // setExpandedTopDistricts(true);
                return null;
            }
            return index;
        });
    };

    const toggleParent = (event: any, index: number) => {
        setPresenceState(prev => {
            const isParentSelected = prev[index]?.parent;
            const updated = {
                ...prev,
                [index]: {
                    startYear: myBusinessPresenceConfig[index].presence_start,
                    endYear: myBusinessPresenceConfig[index].presence_end,
                    parent: !isParentSelected,
                    status: !isParentSelected
                        ? myBusinessPresenceConfig[index].status.map((_: any, i: number) => i)
                        : [],
                    params: !isParentSelected
                        ? myBusinessPresenceConfig[index].parameters.map((_: any, i: number) => i)
                        : [],
                    paramSelections:
                        myBusinessPresenceConfig[index].parameters.reduce((acc: Record<number, ParamSelection>, parameter: any, paramIndex: number) => {
                            acc[paramIndex] = {
                                value: parameter.values[0],
                                year: parameter.values[0]?.year[0] || '',
                                selectedOptions: !isParentSelected ? parameter.options?.map((opt: any) => opt.label) || [] : [],
                            };
                            return acc;
                        }, {})

                },
            };

            // const allUnchecked = Object.values(updated).every(cfg => !cfg.parent);

            // if (allUnchecked) toggleSwitch(null, 'MBP', false);

            // setButtonVisible(JSON.stringify(updated) !== JSON.stringify(initialPresenceState));
            return updated;
        });
    };

    const toggleStatus = (index: number, statusIndex: number) => {
        setPresenceState(prev => {
            const config = prev[index];
            // if (!config?.parent) return prev;

            const selected = config.status;
            const isSelected = selected.includes(statusIndex);

            let updatedStatus = [];

            if (isSelected) {
                if (selected.length === 1) {
                    const updated = {
                        ...prev,
                        [index]: {
                            ...config,
                            status: [],
                            parent: false,
                        },
                    };

                    const allStatusUnchecked = Object.values(updated).every(item => item.parent === false);
                    if (allStatusUnchecked) {
                        toggleSwitch(null, 'MBP', false);
                    }
                    return updated;
                }
                updatedStatus = selected.filter(s => s !== statusIndex);
            } else {
                updatedStatus = [...selected, statusIndex];
            }

            const updated = {
                ...prev,
                [index]: {
                    ...config,
                    status: updatedStatus,
                    parent: true,
                },
            };

            if (updatedStatus.length === 0 && !isChecked.MBP) toggleSwitch(null, 'MBP', false);

            // setButtonVisible(JSON.stringify(updated) !== JSON.stringify(initialPresenceState));
            return updated;
        });
    };

    const toggleParam = (index: number, paramIndex: number) => {
        setPresenceState(prev => {
            const config = prev[index];
            if (!config?.parent) return prev;

            const selected = config.params;
            const isSelected = selected.includes(paramIndex);

            const updatedParams = isSelected
                ? selected.filter(p => p !== paramIndex)
                : [...selected, paramIndex];

            const updatedParamSelections = { ...config.paramSelections };

            const defaultParamConfig = myBusinessPresenceConfig[index]?.parameters[paramIndex];

            updatedParamSelections[paramIndex] = {
                value: defaultParamConfig.values[0],
                year: defaultParamConfig?.values[0]?.year[0],
                selectedOptions: isSelected ? [] : defaultParamConfig?.options.map((opt: any) => opt.label),
            }

            const updated = {
                ...prev,
                [index]: {
                    ...prev[index],
                    status: config.status || [],
                    params: updatedParams,
                    paramSelections: updatedParamSelections,
                },
            };

            // setButtonVisible(JSON.stringify(updated) !== JSON.stringify(initialPresenceState));
            return updated;
        });
    };

    const getMbpPayload = (rawPayload: Record<number, PresenceStateItem>, mbpConfig: any) => {
        const payload = {
            geocode: catchment ? (catchment) : geoCode,
            adjacency: isChecked?.adjacency,
                custom_choropleth_ids:{...dynamicToggles},
            data: Object.entries(rawPayload)
                .filter(([_, config]) => config.parent)
                .map(([indexStr, config]) => {
                    const index = Number(indexStr);
                    const rawConfig = mbpConfig[index];

                    return {
                        presence_type: rawConfig?.presence_type,
                        status: config?.status.map(statusIndex => rawConfig?.status[statusIndex]?.label),
                        // presence_start: config?.startYear || rawConfig?.presence_start,
                        presence_start: config?.startYear || rawConfig?.presence_start,
                        presence_end: config?.endYear || rawConfig?.presence_end,
                        parameters: config?.params?.map(paramIndex => {
                            const param = rawConfig?.parameters[paramIndex];
                            const selection = config?.paramSelections?.[paramIndex];

                            return {
                                param_name: param?.param_name.label,
                                type: selection?.value?.key || param.values[0]?.key,
                                year: Number(selection?.year) || Number(param.values[0]?.year?.[0]),
                                values: selection?.selectedOptions || param.options?.map((opt: any) => opt.label) || []
                            };
                        })
                    };
                }),
        };

        localStorage.setItem('mbp-filter', JSON.stringify(payload));
        setMbpFilter(payload);
        return payload;
    }

    const handleApply = () => {
        updateMbpConfig(presenceState, myBusinessPresenceConfig);
        const payload = getMbpPayload(presenceState, myBusinessPresenceConfig);
        fetchMyPresence(payload);
        setInitialPresenceState(presenceState);
        setButtonVisible(false);
    };

    const handleCancel = () => {
        setPresenceState(initialPresenceState);
        setButtonVisible(false);
    };

    const handleParameterChange = (index: number, paramIndex: number, selectedLabel: string, valueList: any[], options: any[]) => {
        const selectedValue = valueList.find((item) => item.label === selectedLabel);
        if (selectedValue) {
            setPresenceState((prev) => {
                const updatedPresenceState = { ...prev };

                const paramSelection = updatedPresenceState[index]?.paramSelections || {};

                const updatedParamSelections = {
                    ...paramSelection,
                    [paramIndex]: {
                        value: selectedValue,
                        year: selectedValue.year[0] || '',
                        selectedOptions: options.map((opt: any) => opt.label),
                    },
                };

                updatedPresenceState[index] = {
                    ...updatedPresenceState[index],
                    paramSelections: updatedParamSelections,
                };

                return updatedPresenceState;
            });
        }
    };

    const handleYearChange = (index: number, paramIndex: number, year: string) => {
        setPresenceState((prev) => {
            const updatedPresenceState = { ...prev };

            const paramSelection = updatedPresenceState[index]?.paramSelections || {};

            const updatedParamSelections = {
                ...paramSelection,
                [paramIndex]: {
                    ...paramSelection[paramIndex],
                    year,
                },
            };

            updatedPresenceState[index] = {
                ...updatedPresenceState[index],
                paramSelections: updatedParamSelections,
            };

            return updatedPresenceState;
        });
    };

    const toggleOptionSelection = (presenceIndex: number, paramIndex: number, optionLabel: string) => {
        setPresenceState(prev => {
            const presence = prev[presenceIndex];
            if (!presence) return prev;

            const paramSelection = presence.paramSelections?.[paramIndex] ?? { selectedOptions: [] };
            const alreadySelected = paramSelection.selectedOptions.includes(optionLabel);

            const updatedOptions = alreadySelected
                ? paramSelection.selectedOptions.filter(opt => opt !== optionLabel)
                : [...paramSelection.selectedOptions, optionLabel];

            const updatedParamSelections = {
                ...presence.paramSelections,
                [paramIndex]: { ...paramSelection, selectedOptions: updatedOptions },
            };

            const updatedParams = updatedOptions.length
                ? [...new Set([...presence.params, paramIndex])]
                : presence.params.filter(p => p !== paramIndex);

            return {
                ...prev,
                [presenceIndex]: {
                    ...presence,
                    paramSelections: updatedParamSelections,
                    params: updatedParams,
                },
            };
        });
    };

    const generateOptions = (startYear: number, endYear: number, index: number) => {
        const config = myBusinessPresenceConfig[index];

        if (!config.presence_start || !config.presence_end) {
            console.warn(`No config found for index ${index}`);
            return { startOptions: [], endOptions: [] };
        }

        const { presence_start, presence_end } = config

        const startOptions = Array.from(
            { length: endYear - presence_start + 1 },
            (_, i) => ({ value: presence_start + i })
        );
        const endOptions = Array.from(
            { length: presence_end - startYear + 1 },
            (_, i) => ({ value: startYear + i })
        );

        return { startOptions, endOptions };
    };


    const updateOpenYears = (startYear: number, endYear: number, configIndex: number,) => {
        if (configIndex === null && myBusinessPresenceConfig?.length > 0) return;

        const newOpenYears = generateOptions(startYear, endYear, configIndex);

        setOpenYears(newOpenYears);
    };

    const handleStartChange = (value: any, configIndex: number) => {
        if (configIndex === null || myBusinessPresenceConfig?.length === 0) return;

        const { presence_end } = myBusinessPresenceConfig[configIndex!];

        const selected = parseInt(value);
        let newStartYear = selected;
        let newEndYear = Number(presenceState[configIndex].endYear);

        // if (newStartYear >= newEndYear) {
        //     newEndYear = Math.min(newStartYear , Number(presence_end));
        // }

        setPresenceState((prev) => {
            const updatedConfig = prev[configIndex];
            const updatedState = {
                ...prev,
                [configIndex]: {
                    ...updatedConfig,
                    startYear: newStartYear,
                    endYear: newEndYear,
                },
            };

            return updatedState;
        });

        updateOpenYears(newStartYear, newEndYear, configIndex);
    };

    const handleEndChange = (value: any, configIndex: number) => {
        if (configIndex === null || myBusinessPresenceConfig?.length === 0) return;

        const { presence_start } = myBusinessPresenceConfig[configIndex];

        const selected = parseInt(value);
        let newEndYear = selected;
        let newStartYear = Number(presenceState[configIndex].startYear);

        // if (newEndYear <= newStartYear) {
        //     newStartYear = Math.max(Number(presence_start), newEndYear );
        // }

        setPresenceState((prev) => {
            const updatedConfig = prev[configIndex];
            const updatedState = {
                ...prev,
                [configIndex]: {
                    ...updatedConfig,
                    startYear: newStartYear,
                    endYear: newEndYear,
                },
            };

            return updatedState;
        });

        updateOpenYears(newStartYear, newEndYear, configIndex);
    };

    const updatePresenceState = (mbpConfig: any) => {
        const state: Record<number, PresenceStateItem> = {};

        mbpConfig.forEach((presenceConfig: any, index: number) => {
            const statusIndexes = presenceConfig.status.reduce((acc: number[], item: any, i: number) => {
                if (item.isSelected) acc.push(i);
                return acc;
            }, []);

            const paramIndexes = presenceConfig.parameters.reduce((acc: number[], item: any, i: number) => {
                if (item.isSelected) acc.push(i);
                return acc;
            }, []);

            const initialParamSelections: Record<number, ParamSelection> = {};

            presenceConfig.parameters.forEach((parameter: any, paramIndex: number) => {
                // const defaultVal = parameter.values?.[0];  // Assuming the first value is the default
                let selIndex = parameter.values?.findIndex((item: any) => item.isSelected === true);

                if (selIndex === -1) {
                    selIndex = 0;
                }

                initialParamSelections[paramIndex] = {
                    value: parameter.values[selIndex] || { label: '', year: [] },
                    year: parameter.values[selIndex].selectedYear || parameter.values[selIndex].year?.[0],
                    selectedOptions: parameter.options?.reduce((acc: number[], item: any, i: number) => {
                        if (item.isSelected) acc.push(item.label);
                        return acc;
                    }, []),
                };
            });

            state[index] = {
                parent: presenceConfig.isSelected,
                status: statusIndexes,
                params: paramIndexes,
                paramSelections: initialParamSelections,
                startYear: presenceConfig.selectedStart,
                endYear: presenceConfig.selectedEnd,
            };
        });

        return state;
    }

    const updateMbpConfig = (presenceState: Record<number, PresenceStateItem>, mbpConfig: any[]) => {
        const updatedConfig = [...mbpConfig];

        Object.entries(presenceState).forEach(([indexStr, config]) => {
            const index = Number(indexStr);
            const item = updatedConfig[index];

            const isParentEnabled = !!config.parent;
            item.isSelected = isParentEnabled;
            item.selectedStart = config.startYear;
            item.selectedEnd = config.endYear;

            //update status
            if (Array.isArray(item.status)) {
                item.status.forEach((statusItem: any, index: number) => {
                    statusItem.isSelected = isParentEnabled && config.status?.includes(index);
                });
            }

            //update parameters
            if (Array.isArray(item.parameters)) {
                item.parameters.forEach((param: any, paramIndex: number) => {
                    const isParamEnabled = config.params?.includes(paramIndex);
                    param.isSelected = isParentEnabled && isParamEnabled;

                    const selection = config.paramSelections?.[paramIndex];

                    //update values
                    if (Array.isArray(param.values)) {
                        const selectedKey = selection?.value?.key;
                        param.values.forEach((value: any, index: number) => {
                            value.isSelected = !isParentEnabled && !isParamEnabled ? (index === 0 ? true : false) : (isParamEnabled ? value.key === selectedKey : (index === 0 ? true : false));
                            value.selectedYear = isParentEnabled && isParamEnabled ? selection?.year : value.selectedYear;
                        });
                    }

                    //update options
                    if (Array.isArray(param.options)) {
                        const selectedOptions = selection?.selectedOptions || [];
                        param.options.forEach((option: any) => {
                            option.isSelected = isParentEnabled && isParamEnabled && selectedOptions.includes(option.label);
                        });
                    }
                })
            }
        });

        localStorage.setItem('mbp-config', JSON.stringify(updatedConfig));
        setMyBusinessPresenceConfig(updatedConfig);
    }

    useEffect(() => {
        if (expandedPresenceConfig === null || myBusinessPresenceConfig?.length === 0) return;

        const config = myBusinessPresenceConfig[expandedPresenceConfig];
        if (!config) {
            console.warn(`No config found for index ${expandedPresenceConfig}`);
            return;
        }
        const { presence_start, presence_end } = config;

        if (Object.keys(presenceState).length > 0) {
            setPresenceState((prev) => {
                const updatedConfig = prev[expandedPresenceConfig];
                const start: any = updatedConfig?.startYear ? updatedConfig?.startYear : Number(presence_start);
                const end: any = updatedConfig?.endYear ? updatedConfig?.endYear : Number(presence_end);
                const updatedState = {
                    ...prev,
                    [expandedPresenceConfig]: {
                        ...updatedConfig,
                        startYear: start,
                        endYear: end
                    },
                };
                updateOpenYears(start, end, expandedPresenceConfig);

                return updatedState;
            });
        }
    }, [expandedPresenceConfig]);

    const handleStateChange = (mbpConfig: any) => {
        if (!mbpConfig || mbpConfig?.length === 0) return;

        const state = updatePresenceState(mbpConfig);

        setPresenceState(state);
        const payload = getMbpPayload(state, mbpConfig);
        if (equal(payload, mbpPayloadRef.current)) return;

        mbpPayloadRef.current = payload;
        fetchMyPresence(payload);
        setInitialPresenceState(state);
        setExpandedPresenceConfig(null);
        setButtonVisible(false);
    };

    useEffect(() => {
        if (!geoCode) return;

        const mbpConfig = JSON.parse(localStorage.getItem('mbp-config')!);

        if (!mbpConfig) {
            // setSpinner(prev => ({ ...prev, count: prev.count + 1 }));
            cifService.getMyPresenceConfig(geoCode)
                .then((res: any) => {
                    const updatedConfig = checkIsChecked(res);
                    localStorage.setItem('mbp-config', JSON.stringify(updatedConfig));
                    setMyBusinessPresenceConfig(updatedConfig);
                    handleStateChange(updatedConfig);
                })
                .catch((error: any) => getErrorMsg(error))
            // .finally(() => setSpinner(prev => ({ ...prev, count: prev.count - 1 })));
        } else {
            setMyBusinessPresenceConfig(mbpConfig);
            handleStateChange(mbpConfig);
        }
    }, [geoCode]);

    useEffect(() => {
        if (!geoCode || myBusinessPresenceConfig?.length === 0 || !isChecked?.MBP) return;

        const payload = getMbpPayload(presenceState, myBusinessPresenceConfig);
        if (equal(payload, mbpPayloadRef.current)) return;

        mbpPayloadRef.current = payload;
        fetchMyPresence(payload);
        setInitialPresenceState(presenceState);
        if (catchment || geoCode !== (selectedGeo?.village)) {
        }
    }, [geoCode, myBusinessPresenceConfig, isChecked?.adjacency, catchment]);

    useEffect(() => {
        if (!geoCode || Object.keys(presenceState).length === 0 || !myBusinessPresenceConfig || myBusinessPresenceConfig.length === 0) return;

        // Always update mbpFilter when presenceState changes to ensure API is triggered
        // This is needed even when presenceState equals initialPresenceState (e.g., when unchecking all checkboxes)
        const payload = getMbpPayload(presenceState, myBusinessPresenceConfig);
        if (!equal(payload, mbpPayloadRef.current)) {
            mbpPayloadRef.current = payload;
            // Update config whenever payload changes
            updateMbpConfig(presenceState, myBusinessPresenceConfig);
            // Call fetchMyPresence to trigger getbranchnew API
            fetchMyPresence(payload);
        }
    }, [presenceState]);

    // useEffect(() => {
    //     const hasChanged = !deepEqual(presenceState, initialPresenceState);
    //     setButtonVisible(hasChanged);
    // }, [presenceState, initialPresenceState]);

    useEffect(() => {
        if (!isChecked.MBP) {
            setExpandedPresenceConfig(null);
            // When MBP toggle is turned off, update mbpFilter with empty data to trigger mappropertiesv5 API
            if (geoCode && myBusinessPresenceConfig) {
                const emptyPayload = {
                    geocode: catchment ? (catchment) : geoCode,
                    adjacency: isChecked?.adjacency,
                    ...dynamicToggles,
                    data: []
                };
                localStorage.setItem('mbp-filter', JSON.stringify(emptyPayload));
                setMbpFilter(emptyPayload);
                mbpPayloadRef.current = emptyPayload;
            }
        } else {
            // When MBP toggle is turned on, make API call even if all checkboxes are unchecked
            if (Object.keys(presenceState).length > 0 && myBusinessPresenceConfig) {
                const payload = getMbpPayload(presenceState, myBusinessPresenceConfig);
                if (!equal(payload, mbpPayloadRef.current)) {
                    mbpPayloadRef.current = payload;
                    fetchMyPresence(payload);
                    setInitialPresenceState(presenceState);
                }
            }
        }
    }, [isChecked.MBP]);

    useEffect(() => {
        function handleClickOutside(event: any) {
            if (panelRef.current && !panelRef.current.contains(event.target)) {
                setExpandedPresenceConfig(null); // Close panel on outside click
            }
        }

        document.addEventListener("mousedown", handleClickOutside);
        return () => {
            document.removeEventListener("mousedown", handleClickOutside);
        };
    }, []);

    return (
        <>
            {myBusinessPresenceConfig.length > 0 &&
                <div className="d-flex flex-column justify-content-start align-items-start p-0  gap-1 ">
                    {myBusinessPresenceConfig.map((presenceConfig: any, index: number) => {
                        const config = presenceState[index];
                        if (!config) return null;

                        const isParentChecked = config.parent;
                        // If in catchment view, directly use count from API response without additional checks
                        const presenceCount = catchment
                            ? presenceTypeCount?.find((item: any) => item.presence_type === presenceConfig.presence_type)?.count
                            : presenceTypeCount?.find((item: any) => ((selectedGeo.district) === geoCode) ? (item.presence_type === presenceConfig.presence_type && item.sublevel === selectedTab) : (item.presence_type === presenceConfig.presence_type))?.count;
                        return (
                            <div key={index} className={`${expandedPresenceConfig === index ? "bg-light-grey" : ""} w-100 h-100 d-flex flex-column justify-content-start align-items-center p-1`}
                                style={{ backgroundColor: expandedPresenceConfig === index ? "bg-light-grey" : 'none' }}>
                                <div className="w-100 d-flex flex-row justify-content-between align-items-center" style={{opacity:isChecked?.MBP ? "1" : "0.5"}}>
                                    <label htmlFor={`presence-checkbox-${index}`} className="d-flex align-items-center cursor-pointer">
                                        <input
                                            type="checkbox"
                                            id={`presence-checkbox-${index}`}
                                            className="margin-right-3 input-tick"
                                            checked={isParentChecked}
                                            onChange={(event) => toggleParent(event, index)}
                                            disabled={!isChecked?.MBP}
                                        />
                                    </label>
                                    <div className="d-flex align-items-center presence-type-label-width mbp-tooltip-parent position-relative">
                                        <div className="branch-indicator-container margin-right-2">
                                            <img src={presenceConfig.icon} alt="my business presence" />
                                        </div>
                                        <Typography variant="b3" className="m-0 p-0 truncate text-[var(--ds-solid-neutral-11)]">
                                            {presenceConfig?.presence_type}
                                        </Typography>
                                        {presenceTypeCount == null ?
                                            <div className='width-13 position-relative margin-left-2 h-2vh'>
                                                <div className="d-flex justify-content-center align-items-center shimmer position-absolute"></div>
                                            </div> :
                                            <Typography variant="b2" className="m-0 p-0 text-muted-foreground">
                                                {`\u00A0(${presenceCount ? presenceCount : 0})`}
                                            </Typography>
                                        }
                                        {/* tooltip */}
                                        <div className="mbp-tooltip padding-1 left-30">
                                            <Typography variant="b3" className="p-0 text-start text-white">
                                                {presenceConfig?.presence_type}
                                            </Typography>
                                        </div>
                                    </div>
                                    <CaretRight
                                        size={12}
                                        className="cursor-pointer icon-gray-0 shrink-0"
                                        onClick={() => toggleExpand(index)}
                                    />
                                </div>
                                {/* Expanded content */}
                                {expandedPresenceConfig === index && <div ref={panelRef} className=" mbp-popup-home width-15vw" style={{ left: '16.677%', top: '11.5%' }}>
                                    <div className="flex items-center justify-between px-4 pt-3 pb-2">
                                        <Typography variant="b1" className="font-semibold">
                                            {presenceConfig?.presence_type}
                                        </Typography>
                                        <ShadcnButton variant="ghost" size="mini" onClick={() => setExpandedPresenceConfig(null)}>
                                            <Close size={16} />
                                        </ShadcnButton>
                                    </div>
                                    {expandedPresenceConfig === index &&
                                        presenceConfig?.status?.length > 0
                                        && (
                                            <div className="expanded-content d-flex flex-column justify-content-start align-items-start width-50  padding-left-right-4 " style={{ height: '13.464vh' }}>
                                                {/* <hr className="w-100 margin-top-bottom-4" /> */}
                                                <div className="w-100">
                                                    <Typography variant="b3" className="text-muted-foreground text-wrap line-height-14px">
                                                        Current Status
                                                    </Typography>
                                                    <div
                                                        className="d-flex flex-wrap gap-2 height-4vh align-items-center"
                                                    >
                                                        {presenceConfig.status.map((status: any, statusIndex: number) => {
                                                            const isSelected = config.status.includes(statusIndex);
                                                            return (
                                                                <div
                                                                    key={statusIndex}
                                                                    style={{
                                                                        flex: '0 0 calc(35% - 0.25rem)',
                                                                        width: '100%'
                                                                    }}
                                                                >
                                                                    <Chip
                                                                        key={statusIndex}
                                                                        label={status?.label}
                                                                        iconUrl={status?.image}
                                                                        selected={isSelected}
                                                                        disabled={!isParentChecked}
                                                                        onClick={() => toggleStatus(index, statusIndex)}
                                                                        className="chipIcon"
                                                                    />
                                                                </div>
                                                            );
                                                        })}
                                                    </div>
                                                    <Typography variant="b3" className="text-muted-foreground text-wrap line-height-14px">
                                                        Open Year
                                                    </Typography>
                                                    <div className="d-flex w-100 gap-2 height-4vh align-items-center">
                                                        <div className="d-flex flex-column">
                                                            <CustomDropdown
                                                                options={openYears.startOptions}
                                                                // selectedValue={presenceConfig?.presence_start}
                                                                selectedValue={config.startYear}
                                                                disabled={!isParentChecked}
                                                                onSelect={(year) => handleStartChange(year, index)}
                                                                labelKey="value"
                                                                valueKey="value"
                                                                name='year'
                                                                classname="custom-dropdown header-icon w-100 ff-poppins-regular width-6-5vw  height-3vh"
                                                            />
                                                        </div>
                                                        <div className="d-flex flex-column">
                                                            <CustomDropdown
                                                                options={openYears.endOptions}
                                                                // selectedValue={presenceConfig?.presence_end}
                                                                selectedValue={config.endYear}
                                                                disabled={!isParentChecked}
                                                                onSelect={(year) => handleEndChange(year, index)}
                                                                labelKey="value"
                                                                valueKey="value"
                                                                name='year'
                                                                classname="custom-dropdown height-3vh header-icon  ff-poppins-regular width-6-5vw "
                                                            />
                                                        </div>
                                                    </div>
                                                </div>
                                            </div>
                                        )
                                    }
                                    <hr className="mt-1 mb-0 p-0" />
                                    {expandedPresenceConfig === index && (
                                        <div>
                                            {presenceConfig.parameters.map((parameter: any, paramIndex: number) => {
                                                const selected = config?.paramSelections ? config?.paramSelections[paramIndex] : {};
                                                return (
                                                    <div
                                                        className={`padding-4 ${presenceConfig.parameters.length - 1 !== paramIndex ? 'border-bottom' : ''}`}
                                                    >
                                                        <PresenceParameter
                                                            parameter={parameter}
                                                            paramIndex={paramIndex}
                                                            index={index}
                                                            selected={selected}
                                                            configParams={config.params}
                                                            isParentChecked={isParentChecked}
                                                            toggleParam={toggleParam}
                                                            handleParameterChange={handleParameterChange}
                                                            handleYearChange={handleYearChange}
                                                            toggleOptionSelection={toggleOptionSelection}
                                                        />

                                                    </div>
                                                )
                                            })}

                                        </div>
                                    )}
                                </div>}
                            </div>
                        );
                    })}
                </div>
            }
            {/* {buttonVisible && isChecked.MBP && (
                <div className="w-100 d-flex flex-row justify-content-center align-items-center gap-3 margin-top-4">
                    <Button
                        theme={ButtonTheme.secondary}
                        variant={ButtonVariant.bordered}
                        type="button"
                        onClick={handleCancel}
                        classname="border"
                    >
                        Cancel
                    </Button>
                    <Button
                        theme={ButtonTheme.primary}
                        variant={ButtonVariant.bordered}
                        type="button"
                        onClick={handleApply}
                    >
                        Apply
                    </Button>
                </div>)
            } */}
        </>
    );
};